/** Family 4: the offset grid — the row-major layout mode of the shared case generator (plan §4.7). */
import { frontsParameterDef } from '../fronts';
import jointsJson from './joints.json' with { type: 'json' };
import type { Family } from '../registry-types';
import type { FamilyDefinition } from '../types';
import { mm } from '../../domain/units';
import { baseParameterDefs, setBaseParam, sideParameterDefs } from '../base';
import { generateOffset } from './generate';
import { checkOffsetParams, OFFSET_INTAKE, offsetDesignFromFit, offsetFitSummary, type OffsetFitParams } from './intake';
import { createOffset, MAX_ROWS, OFFSET_ID, OFFSET_LABEL, OFFSET_PRESETS, OFFSET_VERSION, offsetParamsOf, setOffsetParam, setPattern, setRows, type OffsetParams, type OffsetPattern, type OffsetPresetKey } from './design';
import { OFFSET_JOINTS } from './generate';

export const OFFSET_DEFINITION: FamilyDefinition = Object.freeze({
  id: OFFSET_ID,
  version: OFFSET_VERSION,
  label: OFFSET_LABEL,
  description: 'A closed case with full-width shelves and short dividers that change position row to row.',
  parameters: [
    { key: 'W', type: 'length', fieldKey: 'W', range: { min: mm(500), max: mm(2400), step: 10 }, default: mm(1200), unit: 'mm', critical: true, ui: { label: 'Width', control: 'field', group: 'Overall size' } },
    { key: 'H', type: 'length', fieldKey: 'H', range: { min: mm(600), max: mm(2200), step: 10 }, default: mm(1800), unit: 'mm', critical: true, ui: { label: 'Height', control: 'field', group: 'Overall size' } },
    { key: 'D', type: 'length', fieldKey: 'D', range: { min: mm(120), max: mm(450), step: 10 }, default: mm(300), unit: 'mm', critical: true, ui: { label: 'Depth', control: 'field', group: 'Overall size' } },
    { key: 'rows', type: 'count', range: { min: 1, max: MAX_ROWS, step: 1 }, default: 5, unit: 'count', critical: false, ui: { label: 'Rows', help: 'Changing the count makes the rows equal and refills the pattern.', control: 'stepper', group: 'Rows' } },
    {
      key: 'pattern', type: 'enum', default: 'brick-bond', critical: false,
      options: [
        { value: 'brick-bond', label: 'Brick-bond', help: 'Rows alternate, offset by half a cell.' },
        { value: 'alternating', label: 'Alternating', help: 'The dividers shift side to side row by row.' },
        { value: 'free', label: 'Free', help: 'Each row keeps its own dividers.' },
      ],
      ui: { label: 'Pattern', help: 'Dividers are added until no shelf spans more than the span limit.', control: 'choice', group: 'Rows' },
    },
    { key: 'rowDividerXs', type: 'list', default: null, critical: false, ui: { label: 'Dividers per row', help: 'Bottom to top: where each short divider stands.', control: 'rows', group: 'Rows' } },
    {
      key: 'back', type: 'enum', default: 'none', critical: false,
      options: [
        { value: 'none', label: 'None', help: 'Open back: the housings and a wall fixing stop racking.' },
        { value: 'overlay', label: 'Overlay', help: 'One back screwed over the back edges.' },
        { value: 'rebated', label: 'Rebated', help: 'One back set into a rebate.' },
        { value: 'per-cell', label: 'Some cells', help: 'Small backs in the cells you choose.' },
      ],
      ui: { label: 'Back', control: 'choice', group: 'Construction' },
    },
    ...sideParameterDefs(),
    ...baseParameterDefs(),
    frontsParameterDef(),
    {
      key: 'stock', type: 'enum', fieldKey: 'stockThickness', default: 'birch-ply-18', critical: true,
      options: [
        { value: 'birch-ply-18', label: 'Birch plywood 18 mm' },
        { value: 'sheesham-board-25', label: 'Sheesham 25 mm' },
        { value: 'oak-board-20', label: 'Oak 20 mm' },
        { value: 'sheesham-board-38', label: 'Sheesham 38 mm' },
        { value: 'sheesham-board-45', label: 'Sheesham 45 mm' },
        { value: 'oak-board-38', label: 'Oak 38 mm' },
        { value: 'oak-board-45', label: 'Oak 45 mm' },
        { value: 'sheesham-board-70', label: 'Sheesham 70 mm slab' },
        { value: 'oak-board-70', label: 'Oak 70 mm slab' },
      ],
      ui: { label: 'Material', help: 'Chosen in Look.', control: 'choice', group: 'Construction', step: 'look' },
    },
  ],
  presets: (Object.entries(OFFSET_PRESETS) as [string, (typeof OFFSET_PRESETS)[OffsetPresetKey]][]).map(([id, p]) => ({ id, label: p.label, description: p.description, params: { W: p.W, H: p.H, D: p.D, ...p.params } })),
  handles: [],
}) as FamilyDefinition;

export const OFFSET_FAMILY: Family<OffsetFitParams> = {
  definition: OFFSET_DEFINITION,
  jointsJson,
  intake: OFFSET_INTAKE,
  generate: (design, stock, construction) => generateOffset(design, stock, construction),
  checkParams: checkOffsetParams,
  designFromFit: offsetDesignFromFit,
  fitSummary: offsetFitSummary,
  create: (presetId?: string) => createOffset((presetId as OffsetPresetKey) ?? 'brick-bond'),
  setParam: (design, key, value) => {
    // patterns add dividers until no cell spans more than the span limit
    const maxCellFrac = ((OFFSET_JOINTS.limits?.maxSpanMm ?? 800) - 20) / (design.W / 10);
    const b = setBaseParam(offsetParamsOf(design), key, value);
    if (b) return { ...design, appearance: { ...design.appearance }, params: b as unknown as Record<string, unknown> };
    if (key === 'rows') return setRows(design, Number(value), maxCellFrac);
    if (key === 'pattern') return setPattern(design, value as OffsetPattern, maxCellFrac);
    return setOffsetParam(design, key as keyof OffsetParams, value as never);
  },
};

export * from './design';
export * from './generate';
export * from './intake';
